import { useId, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { connectPostsApi, type ConversationScopeKind, type PollView } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { pollMarkerHtml } from "@/lib/pollMarkers";

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 10;

function defaultCloseLocal(): string {
    const d = new Date(Date.now() + 24 * 3_600_000);
    d.setMinutes(0, 0, 0);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * "Create a poll" (Teams' quick poll): a question, 2–10 options, single or
 * multiple choice, optional anonymous results and an optional close time.
 * Creates the poll in this conversation, then posts it as a message through
 * `onPost` (the conversation's normal send path) so everyone gets it like any
 * other message. If posting fails the poll is removed again.
 */
export function PollComposerDialog({ open, onOpenChange, scope, conversationId, onPost }: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    scope: ConversationScopeKind;
    conversationId: string;
    /** Send the poll's message (HTML with the poll marker) into the conversation. */
    onPost: (html: string) => Promise<void>;
}) {
    const token = useAccessToken();
    const formId = useId();
    const [question, setQuestion] = useState("");
    const [options, setOptions] = useState<string[]>(["", ""]);
    const [multi, setMulti] = useState(false);
    const [anonymous, setAnonymous] = useState(false);
    const [autoClose, setAutoClose] = useState(false);
    const [closesAt, setClosesAt] = useState(defaultCloseLocal);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const reset = () => {
        setQuestion("");
        setOptions(["", ""]);
        setMulti(false);
        setAnonymous(false);
        setAutoClose(false);
        setClosesAt(defaultCloseLocal());
        setError(null);
    };

    const cleaned = options.map((o) => o.trim()).filter(Boolean);
    const duplicate = new Set(cleaned.map((o) => o.toLowerCase())).size !== cleaned.length;
    const closeTime = new Date(closesAt).getTime();
    const badClose = autoClose && (!Number.isFinite(closeTime) || closeTime <= Date.now() + 60_000);
    const valid = question.trim().length > 0 && cleaned.length >= MIN_OPTIONS && !duplicate && !badClose;

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!valid || busy) return;
        setBusy(true);
        setError(null);
        let created: PollView | null = null;
        try {
            created = await connectPostsApi.createPoll(scope, conversationId, {
                question: question.trim(),
                options: cleaned,
                allowMultiple: multi,
                isAnonymous: anonymous,
                closesAt: autoClose ? new Date(closesAt).toISOString() : null,
                inline: true
            }, token);
            await onPost(pollMarkerHtml(created.id, created.question));
            reset();
            onOpenChange(false);
        } catch (err) {
            if (created) void connectPostsApi.deletePoll(created.id, token).catch(() => undefined);
            const msg = err instanceof Error ? err.message : "";
            setError(msg && !msg.startsWith("Connect BFF") ? msg : "Couldn't post the poll. Try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
            <DialogContent className="max-h-[90dvh] max-w-lg overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Create a poll</DialogTitle>
                    <DialogDescription>Everyone in this conversation can vote and see the results as they come in.</DialogDescription>
                </DialogHeader>
                <form id={formId} onSubmit={(e) => void submit(e)} className="space-y-4">
                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-muted-foreground">Question</span>
                        <input
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            maxLength={512}
                            required
                            autoFocus
                            placeholder="Where should we have lunch?"
                            className="h-9 w-full rounded-md border bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                        />
                    </label>

                    <fieldset className="space-y-1.5">
                        <legend className="mb-1 text-xs font-medium text-muted-foreground">Options ({MIN_OPTIONS}–{MAX_OPTIONS})</legend>
                        {options.map((o, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                                <input
                                    value={o}
                                    onChange={(e) => setOptions((prev) => prev.map((p, j) => (j === i ? e.target.value : p)))}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && i === options.length - 1 && options.length < MAX_OPTIONS && o.trim()) {
                                            e.preventDefault();
                                            setOptions((prev) => [...prev, ""]);
                                        }
                                    }}
                                    maxLength={256}
                                    aria-label={`Option ${i + 1}`}
                                    placeholder={`Option ${i + 1}`}
                                    className="h-9 min-w-0 flex-1 rounded-md border bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                                />
                                <button
                                    type="button"
                                    onClick={() => setOptions((prev) => prev.filter((_, j) => j !== i))}
                                    disabled={options.length <= MIN_OPTIONS}
                                    aria-label={`Remove option ${i + 1}`}
                                    title="Remove option"
                                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-30"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        ))}
                        {options.length < MAX_OPTIONS ? (
                            <button
                                type="button"
                                onClick={() => setOptions((prev) => [...prev, ""])}
                                className="flex items-center gap-1 rounded-md px-1 py-1 text-xs font-medium text-primary hover:underline"
                            >
                                <Plus className="h-3.5 w-3.5" /> Add option
                            </button>
                        ) : null}
                        {duplicate ? <p className="text-xs text-destructive">Each option must be different.</p> : null}
                    </fieldset>

                    <div className="space-y-2 text-sm">
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={multi} onChange={(e) => setMulti(e.target.checked)} className="h-4 w-4 accent-primary" />
                            Allow multiple answers
                        </label>
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="h-4 w-4 accent-primary" />
                            Anonymous — nobody sees who voted for what
                        </label>
                        <div className="flex flex-wrap items-center gap-2">
                            <label className="flex items-center gap-2">
                                <input type="checkbox" checked={autoClose} onChange={(e) => setAutoClose(e.target.checked)} className="h-4 w-4 accent-primary" />
                                Close automatically
                            </label>
                            {autoClose ? (
                                <input
                                    type="datetime-local"
                                    value={closesAt}
                                    onChange={(e) => setClosesAt(e.target.value)}
                                    aria-label="Close time"
                                    aria-invalid={badClose}
                                    className="h-8 rounded-md border bg-background px-2 text-sm"
                                />
                            ) : null}
                        </div>
                        {badClose ? <p className="text-xs text-destructive">Pick a close time in the future.</p> : null}
                    </div>

                    {error ? <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p> : null}

                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            disabled={busy}
                            className="h-9 rounded-md px-3 text-sm hover:bg-accent disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={!valid || busy}
                            className="flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                            Post poll
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
